import * as React from 'react';
import * as Images from 'pos/Images';
import { selectTicket, SelectExistingTicket, TicketSelection } from 'pos/modules/helpers/ticket-selection';
import { TicketDTO, Res, TicketLocationDTO } from 'core/api';
import { Result, SessionProvider } from 'core/infrastructure';
import { createSaleCentersContent, createMapsContent, SaleLocationStatus, PosStatelessScreenClassComponent, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import { userCanClaimTicket } from 'pos/modules/helpers/ticket-selection';
import { OrderingFacade, RestaurantFacade } from 'pos/facades';
import { CustomerDisplay } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';

const LocationStatus = SaleLocationStatus.create(RestaurantFacade.getRestaurantStatus);
const SaleCentersContent = createSaleCentersContent(LocationStatus);
const MapsContent = createMapsContent(LocationStatus);

type Props = Context & Modal.Actions<TicketDTO>;

class ReopenDocument extends PosStatelessScreenClassComponent<Props> {

	protected getScreenId(): string {
		return 'REOPEN_DOCUMENT';
	}

	protected getTitle(): string {
		return this.props.title;
	}

	protected getImage(): string | SvgIcon {
		return Images.ReopenTicket;
	}

	protected useKeypad() {
		return false;
	}

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		const mode = LocationStatus.getSelection().mode === 'maps'
			? { title: Res.Web.SELECT_SALE_LOCATION_SALE_CENTER_BTN(), image: Images.SaleCenter, action: LocationStatus.selectSaleCenter }
			: LocationStatus.getAllMaps().length ? { title: Res.Web.SELECT_SALE_LOCATION_MAPS_BTN(), image: Images.Map, action: LocationStatus.selectMap } : null;

		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			mode
		];
	}

	private async reopenInTicket(location: TicketLocationDTO, selection: SelectExistingTicket): Promise<Result<TicketDTO, unknown>> {

		const canClaim = await userCanClaimTicket(selection.ticket);
		if (!canClaim)
			return Result.error();

		return this.props.reopenInTicket(location, selection.ticket.Id);
	}

	private async moveOrJoin(selection: TicketSelection, location: TicketLocationDTO) {
		const result = selection.select === 'new'
			? await this.props.reopen(location)
			: await this.reopenInTicket(location, selection);

		if (!result.ok) {
			this.cancel();
			return;
		}

		this.close(result.value);
	}

	private async reopen(location: TicketLocationDTO) {

		const result = await this.props.reopen(location);
		if (!result.ok) {
			this.cancel();
			return;
		}


		this.close(result.value);
	}

	private onSaleLocationSelected = async (location: TicketLocationDTO) => {

		await this.execute({
			action: async () => {
				const tickets = (await OrderingFacade.getExistingTicketsIn(location.SaleCenterId, location.SaleLocationName));

				if (tickets.length === 0) {
					await this.reopen(location);
				}
				else {
					const selectionResult = await selectTicket({ tickets }, 'show-create-new-command', 'hide-ticket-commands');
					if (!selectionResult.ok) {
						// Si no queremos reabrir seguimos en la pantalla de selección de mesa
						return;
					}

					await this.moveOrJoin(selectionResult.value, location);
				}
			}
		});

	};

	protected getContent(): React.ReactNode {
		const selection = LocationStatus.getSelection();

		return <>
			{selection.mode === 'maps'
				? <MapsContent selected={selection.selectedMap} onSaleLocationSelected={this.onSaleLocationSelected} />
				: <SaleCentersContent selected={selection.selectedSaleCenter} onSaleLocationSelected={this.onSaleLocationSelected} />}
		</>;
	}


	onComponentDidMount(): void {
		LocationStatus.registerOnChange(async () => this.ifMounted(() => this.forceUpdate()));
	}

	onComponentWillUnmount(): void {
	}
}

type Context = {
	title: string,
	canReopen: () => Promise<boolean>,
	reopen: (location: TicketLocationDTO) => Promise<Result<TicketDTO>>,
	reopenInTicket: (location: TicketLocationDTO, targetTicketId: number) => Promise<Result<TicketDTO>>
}

export type ReopenDocumentResult = { reopenedTicketId: number };

export default async function reopen(context: Context): Promise<Result<ReopenDocumentResult>> {

	if (!SessionProvider.getCurrent().AllowCashTransactions) {
		await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
		return Result.error();
	}

	const canReopen = await context.canReopen();
	if (!canReopen)
		return Result.error();

	await LocationStatus.initialize();

	const result = await Modal.show(ReopenDocument, { ...context });

	if (result.ok)
		await CustomerDisplay.displayTicketTotal(result.value);

	return result.ok ? Result.ok({ reopenedTicketId: result.value.Id }) : Result.error();
}
